import { predefinedEvents } from '@/composables/events/constants';

/** Operator id of the generic internal-event trigger wrapper. */
export const TRIGGER_INTERNAL_EVENT_ID = 'boxtriggerinternalevent';

/**
 * Resolve an operator's "effective" identity for compatibility checks.
 *
 * `boxtriggerinternalevent` is a wrapper whose real identity is its `event`:
 * each event maps (via `predefinedEvents[event].subcomponent`) to the former
 * trigger box that owns the compatibility rules — e.g. event `visit_page` →
 * `boxvisitpage`, `pass_time` → `boxsitepasstime`. Every other operator is its
 * own id. When the wrapper has no event yet, it falls back to its own id.
 *
 * This is the single source of truth for event ↔ box resolution used by the
 * compatibility engine (both the insertion picker and the validation warnings),
 * so two internal-event triggers with different events are no longer conflated.
 */
export function getEffectiveOperatorId(
  data: { id?: string; event?: string } | null | undefined,
): string {
  const id = data?.id ?? '';
  const event = data?.event;
  if (id === TRIGGER_INTERNAL_EVENT_ID && event && event in predefinedEvents) {
    return predefinedEvents[event].subcomponent;
  }
  return id;
}

/**
 * Box ids of the user-selectable internal events (their compatibility-rule
 * owners). Used by the insertion picker: the internal-event wrapper is offered
 * after a parent when at least one of these boxes is compatible, because the
 * concrete event isn't chosen yet at picker time — the event-aware validator
 * then flags an incompatible event choice.
 */
export function getSelectableEventBoxIds(): string[] {
  return Object.values(predefinedEvents)
    .filter((event) => event.display)
    .map((event) => event.subcomponent);
}
